import {
    useEffect, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';

/**
 * useOpcodeMeta — load richer per-opcode meta (title, description, step count,
 * step kinds, declared `inputs[]`) for every opcode wired to the assistant.
 *
 * Shared between OpCodesSection (for card rendering) and InputMappingSection
 * (for the cascading picker rows). Refetches whenever the opcode_id set
 * changes, plus exposes a `patchMeta` helper so callers can merge fresh
 * data after an inline opcode save without forcing a full refetch.
 */
export default function useOpcodeMeta(opcodes) {
    const {
        oragID, subOragID
    } = useParams();
    const [meta, setMeta] = useState({});
    const [docs, setDocs] = useState({});

    useEffect(() => {
        if (!oragID || !subOragID) return undefined;
        const ids = (opcodes || []).map((o) => o?.opcode_id).filter(Boolean);
        if (ids.length === 0) {
            setMeta({});
            setDocs({});
            return undefined;
        }
        let cancelled = false;
        Promise.all(ids.map((id) =>
            jsonOpcodeService.getJSONOpcodeById({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcodeId: id
            }).then((res) => [id, res?.data || res]).catch(() => [id, null])
        )).then((pairs) => {
            if (cancelled) return;
            const nextMeta = {};
            const nextDocs = {};
            for (const [id, doc] of pairs) {
                if (doc) {
                    nextDocs[id] = doc;
                    nextMeta[id] = {
                        title: doc.title || id,
                        description: doc.description || '',
                        steps_count: Array.isArray(doc.steps) ? doc.steps.length : 0,
                        kinds: Array.isArray(doc.steps)
                            ? [...new Set(doc.steps.map((s) => s.kind))]
                            : [],
                        inputs: Array.isArray(doc.inputs) ? doc.inputs : []
                    };
                } else {
                    nextMeta[id] = {
                        title: id,
                        description: '(opcode not found in your sub-org)',
                        missing: true,
                        inputs: []
                    };
                }
            }
            setMeta(nextMeta);
            setDocs(nextDocs);
        });
        return () => { cancelled = true; };
    }, [oragID, subOragID, JSON.stringify((opcodes || []).map((o) => o?.opcode_id))]); // eslint-disable-line

    function patchMeta(opcodeId, updates) {
        setMeta((m) => ({
            ...m,
            [opcodeId]: {
                ...m[opcodeId],
                ...updates,
                inputs: Array.isArray(updates?.inputs)
                    ? updates.inputs
                    : (m[opcodeId]?.inputs || [])
            }
        }));
    }
    function dropMeta(opcodeId) {
        setMeta((m) => { const next = { ...m }; delete next[opcodeId]; return next; });
        setDocs((d) => { const next = { ...d }; delete next[opcodeId]; return next; });
    }

    function getDoc(opcodeId) {
        return docs[opcodeId] || null;
    }

    function patchDoc(opcodeId, doc) {
        if (!doc) return;
        setDocs((d) => ({ ...d, [opcodeId]: doc }));
    }

    return {
        meta,
        docs,
        getDoc,
        patchDoc,
        patchMeta,
        dropMeta
    };
}
